export function createBlankRender () {
  return {
    response: {
      status: 200,
      rewrite: '',
    },
    page: {},
    template: {
      custom: {},
    },
    layout: {
      fields: {},
    },
    layouts: {},
    branches: {},
    blocks: {},
    links: {},
    plugins: {},
    nodeFields: {},
    styleSyncIDs: {},
    isLayoutV2: false,
    content: {},
    styles: [],
    variables: [],
  }
}

export async function fetchRender (platform, pathname, blockId) {
  const route = platform.storeData.config.globals.routes.render
  const { host } = platform.router
  let res = null

  if (!pathname.includes('?')) {
    const params = platform.router.params.toString()

    if (params) {
      pathname += '?' + params
    }
  }

  pathname = encodeURIComponent(pathname)

  if (!blockId) {
    res = await platform.fetch(`https://${host}${route}?url=${pathname}`)
  } else {
    blockId = encodeURIComponent(blockId)
    res = await platform.fetch(`https://${host}${route}?url=${pathname}&block-id=${blockId}`)
  }

  return res.json()
}

export async function page (platform, pathname, blockId) {
  let status = 200
  let render = createBlankRender()

  try {
    if (!pathname.startsWith('/')) {
      throw new Error('URL must start with /')
    }
    pathname = pathname.toLowerCase()
    const renderData = await fetchRender(platform, pathname, blockId)

    if (renderData.code) {
      // API HTTP error codes will be on the code field and all have 10,000 added onto the normal number
      status = renderData.code - 10000
      render = { status: renderData.message.toLowerCase() }
    } else {
      render = {
        ...render,
        ...renderData,
      }

      if (renderData.content) {
        render.template.custom.layout = 'core/extensions/layout/v3.0.0/Layout'
        render.isLayoutV2 = true

        render.content = renderData.content
        render.styles = renderData.styles || []
        render.variables = renderData.variables || []
      } else {
        render.template.custom.layout = render.template.custom.layout || platform.storeData.config.globals.refs.layout
      }
    }
  } catch (err) {
    console.error(err)
  }

  return {
    status,
    json () {
      return render
    },
  }
}

export async function checkForCustomErrorPage (api, statusCode) {
  try {
    return await api.cms.pages.find({
      where: `page.url = ?`,
      whereArgs: [`/error/${statusCode}`],
    })
  } catch (err) {
    return null
  }
}

export async function isAccessible (platform, pathname) {
  if (!platform.storeData.isOnline) {
    return true
  }

  const redirect = { redirect: platform.storeData.loginUrl }

  if (pathname.startsWith('/cms/')) {
    if (pathname === platform.storeData.loginUrl || pathname === platform.storeData.resetUrl || pathname === platform.storeData.setupUrl) {
      return true
    }

    if (!platform.api.core.isLoggedIn()) {
      return redirect
    }

    return true
  }

  try {
    if (!pathname.startsWith('/')) {
      throw new Error('URL must start with /')
    }

    let status = 200
    const route = platform.storeData.config.globals.routes.accessible
    const { host } = platform.router
    const res = await platform.fetch(`https://${host}${route}?url=${pathname}`)
    const data = await res.json()

    if (data.code) {
      // API HTTP error codes will be on the code field and all have 10,000 added onto the normal number
      status = data.code - 10000
    }

    if (status === 401 || status === 403) {
      return redirect
    }

    return status >= 200 && status < 500
  } catch (err) {
    console.error(err)
  }

  return false
}

const cache = {}

export async function preload (platform, pathname) {
  const { config } = platform.storeData
  let render = createBlankRender()

  if (!pathname.startsWith('/cms/')) {
    const res = await page(platform, pathname)

    cache[pathname] = res

    if (res.status !== 200) {
      return
    }

    render = await res.json()
    cache[pathname].json = async () => render
  }

  const requiredComponents = [
    render.template.custom.layout,
    ...(render.template.custom.components || []),
  ].filter(c => c)

  for (const [, plugin] of Object.entries(render.plugins || {})) {
    requiredComponents.push(plugin.ref)
  }

  await platform.api.core.loadComponents(requiredComponents, { missing: config.components.missing })

  platform.store.update(store => {
    store.components = [...new Set([...store.components, ...requiredComponents])]

    return store
  })
}

export async function load (platform, pathname) {
  const { config } = platform.storeData
  const data = { status: 200, pathname }
  let render = createBlankRender()
  let customErrorPage = null

  if (!pathname.startsWith('/cms/')) {
    let res = null

    if (platform.storeData.isOnline) {
      res = await page(platform, pathname)
    } else {
      res = cache[pathname]
    }

    const pageResponse = await res.json()

    let renderData = null

    if (!pageResponse?.page?.isActive && !platform.api.core.hasScope('super', 'admin')) {
      res.status = 404
    }

    if (res.status !== 200) {
      customErrorPage = await checkForCustomErrorPage(platform.api, res.status)
      data.status = res.status

      if (customErrorPage) {
        const errorRes = await page(platform, customErrorPage.url)
        data.pathname = `/error/${res.status}`
        renderData = errorRes.json()
      } else {
        data.pathname = `/errors/${res.status}`
      }

      platform.store.update(store => {
        store.render = renderData || render
        store.render.response = store.render.response || {}
        store.render.response.status = data.status
        store.render.response.rewrite = data.pathname
        return store
      })

      if (!customErrorPage) {
        return data
      }
    }

    if (res?.components) {
      render.isLayoutV2 = true
    }

    render = renderData || pageResponse
  }

  let requiredComponents = []
  requiredComponents = [
    render.template.custom.layout,
    ...(render.template.custom.components || []),
  ].filter(c => c)

  for (const [, plugin] of Object.entries(render.plugins || {})) {
    requiredComponents.push(plugin.ref)
  }

  await platform.api.core.loadComponents(requiredComponents, { missing: config.components.missing })

  render.plugins = await platform.api.core.initPlugins(render.plugins)

  try {
    const res = await platform.api.cms.entities.findAll({
      where: `bucket.name = ?`,
      whereArgs: ['sharedStyles'],
      limit: {
        count: 1000,
      },
    })

    for (const resource of res.resources) {
      render.styleSyncIDs[resource.name] = {
        id: resource.id,
        bucketID: resource.bucketID,
        bucketName: resource.bucketName,
        payload: JSON.parse(resource.payload) || {},
      }
    }
  } catch {}

  platform.store.update(store => {
    store.render = render
    store.components = [...new Set([...store.components, ...requiredComponents])]

    return store
  })

  return data
}

export async function selectEntry (platform, routes = {}) {
  const { config } = platform.storeData
  const selected = await platform.router.select({
    routes,
    pathname: platform.storeData.render.response.rewrite || platform.router.pathname,
  })

  if (!selected.ref) {
    selected.ref = config.components.missing
  }

  await platform.api.core.loadComponents([selected.ref], { missing: config.components.missing })

  const module = platform.components[selected.ref]

  return module.default
}
